import * as React from 'react';
import {faInfoCircle} from '@fortawesome/pro-solid-svg-icons';
import {faSearchPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ChangeEvent, useEffect, useState} from 'react';

import ImpressionTracker from '../../analytics/impression-tracker/ImpressionTracker';
import {addItem} from '../../items/compare/CompareFunctions';
import {Alerts} from '../../ui/Alerts/Alerts';
import {ContextMenu} from '../../ui/ContextMenu/ContextMenu';
import {ExceptionIcons} from '../../orders/exception-icons/ExceptionIcons';
import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {ImageZoomModal} from '../../items/image-zoom-modal/ImageZoomModal';
import {ImpUrl} from '../../../client/imp-url/imp-url.class';
import {Input} from '../../ui/forms/Input/Input';
import {ItemToAdd} from '../../order-items/order-items.class';
import {ListsWorkflow} from '../../../client/lists/lists.workflow';
import {OrderItemsWorkflow} from '../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../client/orders/orders.service';
import {Price} from '../../pricing/Price';
import {ProductImage} from '../../ui/ProductImage/ProductImage';
import {Result, SearchResult} from '../search.types';
import {ShipRestrictionsModal} from '../../items/ship-restrictions/ShipRestrictionsModal';
import {SignalsService} from '../../../client/search/signals/signals.service';
import {UpdateItemsOnListActionTypes, UpdateListItem} from '../../lists/list.class';
import {UserStateService} from '../../../client/users/user-state.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {Badge} from '../../ui/Badges/Badge';
import Button from '../../ui/Buttons/Button';

interface ItemResultProps {
    className?: string;
    defaultQty?: number;
    gaItemListName: GaItemListName;
    item: Result;
    itemIndex?: number;
    onQtyChange?: (newQty: number) => void;
    position?: number;
    priceLookupError: string;
    resultList: SearchResult;
    showBulk?: boolean;
    user: User;
}

const productListName = `search_results`;

export const ItemResult = ({
    className,
    defaultQty,
    gaItemListName,
    item,
    itemIndex,
    onQtyChange,
    position,
    priceLookupError,
    resultList,
    showBulk,
    user,
}: ItemResultProps) => {
    const componentName = `itemResult-${item.id}`;
    const [qtyOrd, setQtyOrd] = useState(1);
    const [showImageZoom, setShowImageZoom] = useState(false);
    const [shipRestrictionsModal, setShipRestrictionsModal] = useState({
        show: false,
        orderableItem: undefined,
        unitsOrdered: 1,
    });

    const listsWorkflow: ListsWorkflow = useService(`listsWorkflow`);
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const ordersService: OrdersService = useService(`ordersService`);
    const signalsService: SignalsService = useService(`signalsService`);
    const userStateService: UserStateService = useService(`userStateService`);

    // Process queued actions
    useEffect(() => {
        if (userStateService.hasPendingAddToOrder(componentName)) {
            orderItemsWorkflow.addToOrderModal(userStateService.addToOrderItems, componentName, true);
        } else if (userStateService.hasPendingAddToList(componentName)) {
            listsWorkflow.addToList(
                userStateService.addToListAction,
                userStateService.addToListItems,
                componentName,
                componentName,
                userStateService.addToListItemDesc,
                userStateService.addToListItemImage,
            );
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Adds item to comparison feature
     */
    const compareItem = () => {
        addItem(item.id, item.imagePath_s);
    };

    /**
     * TBD
     */
    const itemClickEvents = () => {
        signalsService
            .sendSignals([
                {
                    request_id_s: resultList.request_id,
                    type: `click`,
                    params: {
                        collection: `ImperialItems`,
                        docId: item.id,
                        fusion_query_id: resultList.fusionQueryId,
                        referrer: location.href,
                        res_pos: position,
                        source: productListName,
                        url: `/item/${item.id}`,
                    },
                },
            ])
            .catch((sendSignalsErr) => {
                // fail silently, most likely a navigation before signals finish saving
                // eslint-disable-next-line no-console
                console.log(`Error sending fusion signals`, sendSignalsErr);
            });
    };

    /**
     * Adds items to order
     * @param itemsToAdd - Items to add to the order
     * @private
     */
    const _addToOrder = (itemsToAdd: ItemToAdd[]) => {
        orderItemsWorkflow.addToOrderModal(itemsToAdd, componentName, true);

        // Record analytics
        signalsService
            .sendSignals([
                {
                    type: `add_to_cart`,
                    request_id_s: resultList.request_id,
                    params: {
                        collection: `ImperialItems`,
                        docId: item.id,
                        fusion_query_id: resultList.fusionQueryId,
                        referrer: location.href,
                        res_pos: position,
                        source: productListName,
                    },
                },
            ])
            .catch((sendSignalsErr) => {
                // fail silently, most likely a navigation before signals finish saving
                // eslint-disable-next-line no-console
                console.log(`Error sending fusion signals`, sendSignalsErr);
            });
    };

    /**
     * Adds provided item to list
     * @param action
     * @param updateListItems
     * @param subComponentName
     * @param itemDesc
     * @param itemImage
     * @private
     */
    const addToList = (
        action: UpdateItemsOnListActionTypes,
        updateListItems: UpdateListItem[],
        subComponentName: string,
        itemDesc: string,
        itemImage: string,
    ) => {
        listsWorkflow.addToList(action, updateListItems, subComponentName, subComponentName, itemDesc, itemImage);
    };
    /**
     * Template
     */
    return (
        <div
            className={`tw-relative tw-min-w-0 tw-flex tw-flex-col tw-bg-white tw-border tw-rounded-sm tw-mb-3 tw-w-full${
                className ? ' ' + className : ''
            }`}
        >
            {showBulk && <div className="tw-text-center tw-bg-gray-250">BULK SIZE</div>}
            <div className="tw-p-2">
                <Alerts
                    message={item.isdiscontinue ? `Item has been discontinued. Purchase while supplies last!` : undefined}
                    variant="warning"
                />
                <div className="tw-relative">
                    <div className="tw-flex tw-flex-wrap tw-items-center">
                        <div className="tw-w-1/3 md:tw-w-3/12 tw-order-2 md:tw-order-1">
                            <div>
                                <ImpressionTracker
                                    data={{
                                        dimension16: ordersService?.currentOrderNumber,
                                        index: position,
                                        item_id: item.id,
                                        item_list_name: gaItemListName,
                                    }}
                                    trackImpressions={false}
                                >
                                    <a
                                        href={`/item/${item.id}`}
                                        onClick={itemClickEvents}
                                    >
                                        <div className="tw-relative tw-text-center">
                                            <ProductImage
                                                altText={item.h1HTML}
                                                className="tw-cursor-pointer tw-max-w-full tw-h-auto"
                                                lazy
                                                size={120}
                                                src={item.imagePath_s}
                                            />
                                            {item.hoverImagePath_s && (
                                                <ProductImage
                                                    altText={item.h1HTML}
                                                    containerClass="tw-absolute tw-left-0 tw-right-0 tw-bottom-0 tw-m-auto tw-opacity-0 tw-transition-[opacity_0.375s_ease-out]"
                                                    className="tw-cursor-pointer tw-max-w-full tw-h-auto tw-absolute tw-left-0 tw-right-0 tw-bottom-0 tw-transition-[opacity_0.375s_ease-out] tw-m-auto tw-opacity-0"
                                                    lazy
                                                    size={120}
                                                    src={item.hoverImagePath_s}
                                                />
                                            )}
                                        </div>
                                    </a>
                                </ImpressionTracker>
                            </div>
                        </div>
                        <div className="tw-w-full md:tw-w-6/12 tw-order-1 md:tw-order-2 tw-flex">
                            <div className="tw-grow">
                                {item.prevPurchased && <Badge variant="success">Purchased</Badge>}
                                {item.isBestSeller_b && !item.prevPurchased && !item.pandemicItem && (
                                    <Badge variant="warning">Best Seller</Badge>
                                )}
                                {item.isNew && !item.isJustAdded_b && !item.isBestSeller_b && !item.prevPurchased && !item.pandemicItem && (
                                    <Badge variant="danger">New</Badge>
                                )}
                                {item.isJustAdded_b && !item.isBestSeller_b && !item.prevPurchased && !item.pandemicItem && (
                                    <Badge variant="danger">Just Added</Badge>
                                )}
                                <div>{item.brand_name_s || ' '}</div>
                                <div>
                                    <ImpressionTracker
                                        data={{
                                            dimension16: ordersService?.currentOrderNumber,
                                            index: position,
                                            item_id: item.id,
                                            item_list_name: gaItemListName,
                                        }}
                                        detectPartial={false}
                                    >
                                        <a
                                            href={`/item/${item.id}`}
                                            onClick={itemClickEvents}
                                        >
                                            <strong dangerouslySetInnerHTML={{__html: item.h1HTML}}></strong>
                                        </a>
                                    </ImpressionTracker>
                                </div>
                                <div>
                                    #{item.prettyid}
                                    {item.myItem ? ` | My Item: ${item.myItem}` : ''} | Pkg Qty:{item.pkgQty}
                                </div>
                                {item.pandemicItemBlocked && !item.hasItemFlagsExc && (
                                    <div className="tw-text-red-500">
                                        <FontAwesomeIcon
                                            className="tw-align-text-top"
                                            icon={faInfoCircle}
                                        />{' '}
                                        Unavailable
                                    </div>
                                )}
                                {item.pandemicItemBlocked && item.hasItemFlagsExc && (
                                    <div className="tw-text-red-500">
                                        <FontAwesomeIcon
                                            className="tw-align-text-top"
                                            icon={faInfoCircle}
                                        />{' '}
                                        Limited Availability
                                    </div>
                                )}
                                {!item.pandemicItemBlocked && item.itemQtyLimit_i && (
                                    <div className="tw-block tw-font-bold">
                                        Max Order Qty: {Math.floor(item.itemQtyLimit_i / item.pkgQty)}
                                    </div>
                                )}
                            </div>
                            <div
                                className="tw-w-[106px] md:tw-w-0"
                                data-e2e="ItemResult-context"
                            >
                                <ContextMenu
                                    className="tw-mr-3 tw-mb-3"
                                    index={position}
                                    items={[
                                        {
                                            name: `Add to List`,
                                            disabled: item.isdiscontinue,
                                            onClick: () =>
                                                addToList(
                                                    `add`,
                                                    [
                                                        {
                                                            item: item.id,
                                                            quantity: 1,
                                                        },
                                                    ],
                                                    componentName,
                                                    item.name,
                                                    item.imagePath_s,
                                                ),
                                        },
                                        {
                                            name: `View in Catalog (${(item.catpage || [])[0]})`,
                                            hidden: !item.catpage || user.isCatalogRestricted(),
                                            href: `/flyer/full-line-catalog/${(item.catpage || [])[0]}/${item.prettyid}`,
                                        },
                                        {
                                            name: `View Product Category`,
                                            hidden: resultList.location !== 'Search',
                                            href: `${resultList.baseBcURL}/${(item.breadcrumbs.slice(-1)[0] || {name: ''}).categoryPath}`,
                                        },
                                        {
                                            name: `Compare`,
                                            onClick: compareItem,
                                        },
                                    ]}
                                    position="top"
                                />
                            </div>
                        </div>
                        <div className="tw-w-2/3 md:tw-w-3/12 tw-order-3 tw-text-right tw-min-h-[110px]">
                            <div className="tw-mt-4 tw-text-lg">
                                <Price
                                    alias="SearchResultItem"
                                    className="ImpItem_priceLine tw-mt-14 tw-mb-4"
                                    isError={!!priceLookupError}
                                    item={item}
                                    key={`price_${itemIndex}`}
                                    user={user}
                                />
                                {user.hasOneOfThesePriceBooks(['IL']) && <div>Call for Personalized Pricing</div>}
                                {resultList.debug && user.sortField && `| sortPrice: [${item[user.sortField]}] ${user.sortField}`}
                                {item.subbedFrom && (
                                    <div>
                                        Item ${item.id} substituted for ${item.subbedFrom}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="tw-mb-0 lg:tw-mb-3 tw-flex-wrap lg:tw-flex-nowrap tw-flex">
                        <div className="tw-w-1/3 md:tw-w-3/12 tw-flex tw-justify-center tw-items-center">
                            <a
                                aria-label="Zoom Image"
                                className="tw-cursor-pointer tw-relative"
                                onClick={() => {
                                    setShowImageZoom(true);
                                }}
                            >
                                <FontAwesomeIcon
                                    className="tw-text-gray-400 tw-p-2 tw-mt-1"
                                    icon={faSearchPlus}
                                    size="2x"
                                />
                            </a>
                        </div>
                        <div className="tw-w-1/6 tw-mb-2 tw-self-auto lg:tw-self-center">
                            <ExceptionIcons
                                exceptions={item.exceptions}
                                user={user}
                            />
                        </div>
                        <div className="tw-w-full lg:tw-w-3/5 tw-self-center">
                            <div className="tw-mt-0 lg:tw-mt-4 tw-flex tw-justify-end tw-max-h-[40px]">
                                <Input
                                    aria-label="Quantity"
                                    className="tw-mr-2 !tw-h-10"
                                    clearMargin={true}
                                    defaultValue={(defaultQty || item.unitsOrdered || 1).toString()}
                                    inputMode="numeric"
                                    name="QtyOrd"
                                    onChange={(changeEvent: ChangeEvent<HTMLInputElement>) => {
                                        setQtyOrd(parseInt(changeEvent.target.value));
                                        if (onQtyChange) {
                                            onQtyChange(parseInt(changeEvent.target.value));
                                        }
                                    }}
                                    pattern="[0-9]*"
                                    style={{width: '48px'}}
                                    type="number"
                                />
                                <Button
                                    className="tw-w-auto tw-min-w-[120px]"
                                    data-e2e="ItemResult-ato"
                                    onClick={() => {
                                        if (!user.isLoggedIn() && item.excludeStArray) {
                                            setShipRestrictionsModal({
                                                orderableItem: item,
                                                show: true,
                                                unitsOrdered: qtyOrd,
                                            });
                                        } else {
                                            _addToOrder([
                                                {
                                                    gaEcommerce: {
                                                        item_list_name: gaItemListName,
                                                        promotion_id: resultList.location === `Special` ? ImpUrl.getUrlSegment(2) : ``,
                                                        promotion_name: resultList.location === `Special` ? resultList.title : ``,
                                                    },
                                                    item: item.id,
                                                    unitsOrdered: qtyOrd,
                                                },
                                            ]);
                                        }
                                    }}
                                    size="sm"
                                    variant="primary"
                                >
                                    Add to Order
                                </Button>
                                <div className="tw-hidden md:tw-block tw-py-1.5 tw-px-3">
                                    <Button
                                        className="!tw-pr-0"
                                        disabled={item.isdiscontinue}
                                        onClick={() =>
                                            addToList(
                                                `add`,
                                                [
                                                    {
                                                        item: item.id,
                                                        quantity: 1,
                                                    },
                                                ],
                                                componentName,
                                                item.name,
                                                item.imagePath_s,
                                            )
                                        }
                                        size="sm"
                                        variant="link"
                                    >
                                        Add To List
                                    </Button>
                                    {` | `}
                                    <Button
                                        className="!tw-pl-0"
                                        onClick={compareItem}
                                        size="sm"
                                        variant="link"
                                    >
                                        Compare
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ShipRestrictionsModal
                addToOrder={_addToOrder}
                gaItemListName={gaItemListName}
                onHide={() =>
                    setShipRestrictionsModal({
                        show: false,
                        orderableItem: undefined,
                        unitsOrdered: 1,
                    })
                }
                orderableItem={shipRestrictionsModal.orderableItem}
                show={shipRestrictionsModal.show}
                unitsOrdered={shipRestrictionsModal.unitsOrdered}
                useAddToOrderModal={true}
            />
            <ImageZoomModal
                activeIndex={0}
                onHide={() => {
                    setShowImageZoom(false);
                }}
                resultItem={item}
                show={showImageZoom}
            />
        </div>
    );
};
